import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Download, ExternalLink, Send } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { sendInvoice } from "@/features/invoices/api/invoices";
import { track } from "@/utils/mixpanel";

interface InvoicePdfPreviewProps {
  pdfUrl: string;
  onLoad: () => void;
  onError: () => void;
}

function InvoicePdfPreview({
  pdfUrl,
  onLoad,
  onError,
}: InvoicePdfPreviewProps) {
  // Use Google Docs Viewer to display the PDF
  const googleDocsViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(
    pdfUrl
  )}&embedded=true`;

  return (
    <div className="w-full h-full bg-white">
      <iframe
        src={googleDocsViewerUrl}
        className="w-full h-full"
        onLoad={onLoad}
        onError={onError}
        frameBorder="0"
        title="Invoice PDF Preview"
      />
    </div>
  );
}

interface InvoicePdfPreviewSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pdfUrl: string;
  invoiceNumber?: string;
  invoiceId?: string;
  invoiceStatus?: string;
}

const InvoicePdfPreviewSheet = ({
  open,
  onOpenChange,
  pdfUrl,
  invoiceNumber,
  invoiceId,
  invoiceStatus,
}: InvoicePdfPreviewSheetProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // Determine if the Send button should be shown (only for 'open' status invoices)
  const canSendInvoice = invoiceStatus === "open" && invoiceId;

  // Send invoice mutation
  const { mutate: sendInvoiceMutation, isPending: isSending } = useMutation({
    mutationFn: () => {
      if (!invoiceId) throw new Error("No invoice ID provided");
      return sendInvoice(invoiceId);
    },
    onSuccess: () => {
      toast.success("Invoice sent successfully!");
    },
    onError: (error) => {
      console.error("Error sending invoice:", error);
      toast.error("Failed to send invoice. Please try again.");
    },
  });

  // Reset loading state when the modal opens
  useEffect(() => {
    if (open) {
      setIsLoading(true);
      setLoadError(false);
      console.log("Opening PDF preview sheet with URL:", pdfUrl);
    }
  }, [open, pdfUrl]);

  // Set a timeout to detect if the PDF is taking too long to load
  useEffect(() => {
    if (!open || !isLoading) return;

    const timeoutId = setTimeout(() => {
      if (isLoading) {
        console.log("PDF loading timeout reached");
        setLoadError(true);
        setIsLoading(false);
        toast.error(
          "PDF preview is taking too long to load. Try viewing it directly."
        );
      }
    }, 8000); // Increased timeout for Google Docs Viewer

    return () => clearTimeout(timeoutId);
  }, [open, isLoading]);

  const handleContentLoad = () => {
    console.log("PDF viewer loaded successfully");
    setIsLoading(false);
  };

  const handleContentError = () => {
    console.log("Error loading PDF viewer");
    setLoadError(true);
    setIsLoading(false);
  };

  const handleSendInvoice = () => {
    if (canSendInvoice) {
      toast.loading("Sending invoice...", { id: "sending-invoice" });
      // Track send invoice action
      track("send_invoice", {
        invoice_id: invoiceId,
        invoice_number: invoiceNumber,
      });
      sendInvoiceMutation(undefined, {
        onSettled: () => {
          toast.dismiss("sending-invoice");
        },
      });
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="w-full sm:max-w-4xl p-0 overflow-hidden"
        side="right"
      >
        <div className="h-full flex flex-col">
          <SheetHeader className="p-6 pb-2">
            <SheetTitle className="text-xl font-semibold">
              Invoice Preview {invoiceNumber ? `- ${invoiceNumber}` : ""}
            </SheetTitle>
          </SheetHeader>

          <div className="flex-1 relative overflow-hidden border-t border-b">
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-background/80 z-10">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
                <span className="ml-3 text-sm text-muted-foreground">
                  Loading preview...
                </span>
              </div>
            )}

            {loadError ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center">
                <div className="text-red-500 mb-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                </div>
                <p className="mb-2">
                  Unable to preview the invoice in the application.
                </p>
                <p className="text-sm text-muted-foreground mb-4">
                  This might be due to how the invoice is being served or
                  browser restrictions.
                </p>
              </div>
            ) : (
              <div className="h-[calc(100vh-10rem)]">
                <InvoicePdfPreview
                  pdfUrl={pdfUrl}
                  onLoad={handleContentLoad}
                  onError={handleContentError}
                />
              </div>
            )}
          </div>

          <SheetFooter className="p-6 pt-4 flex justify-end gap-2">
            {canSendInvoice && (
              <Button
                onClick={handleSendInvoice}
                variant="default"
                className="gap-2"
                disabled={isSending}
              >
                <Send className="h-4 w-4" />
                {isSending ? "Sending..." : "Send Invoice"}
              </Button>
            )}
            <Button
              onClick={() => {
                // Track download action
                track("download_invoice", {
                  invoice_id: invoiceId,
                  invoice_number: invoiceNumber,
                  source: "preview_modal",
                });
                // Use window.location.href or an anchor tag to download the invoice if needed
                const a = document.createElement("a");
                a.href = pdfUrl;
                a.download = `invoice-${
                  invoiceNumber || new Date().getTime()
                }.pdf`;
                a.click();
              }}
              className="gap-2"
            >
              <Download className="h-4 w-4" />
              Download Invoice
            </Button>
          </SheetFooter>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default InvoicePdfPreviewSheet;
